import { useState, useEffect, useRef, useCallback } from 'react'
import { X, ChevronLeft, Send, Paperclip, Smile, Plus, Search, MessageSquare } from 'lucide-react'
import { useAuth } from '../context/AuthContext'
import { shouldSendOnEnter } from '../utils/inputKeys'
import {
  listTickets, getTicket, createTicket, sendMessage, reopenTicket, uploadAttachment,
  type Ticket, type TicketDetail, type Message
} from '../api/support'

const CATEGORIES = [
  {
    id: 'general',
    title: 'По общему вопросу',
    hints: ['· что именно вас не устроило', '· когда это произошло', '· какой результат вы ожидаете'],
  },
  {
    id: 'technical',
    title: 'По техническому вопросу',
    hints: ['· какая модель использовалась', '· текст ошибки или скриншот', '· шаги для воспроизведения'],
  },
  {
    id: 'complaint',
    title: 'Для отработки претензии',
    hints: ['· что именно вас не устроило', '· когда это произошло', '· какой результат вы ожидаете'],
  },
]

const SERVICES = {
  'Текстовые': ['ChatGPT (GPT-4o)', 'ChatGPT (GPT-4o mini)', 'ChatGPT (GPT-5)', 'Claude Sonnet', 'Claude Opus', 'Gemini Pro', 'Gemini Flash', 'DeepSeek Chat', 'DeepSeek Reasoner'],
  'Изображения': ['Midjourney', 'Flux', 'Nano Banana', 'Stable Diffusion', 'DALL-E'],
  'Видео': ['Kling', 'Veo', 'Sora', 'Runway', 'Hailuo', 'Seedance'],
}

const EMOJIS = ['😀','😃','😄','😁','😅','😂','🤣','😊','😇','🙂','😉','😌','😍','🥰','😘','😗','😙','😚','😋','😛','😜','🤪','😝','🤑','🤗','🤭','🤫','🤔','😐','😑','😶','😏','😒','🙄','😬','😮','😯','😲','😳','🥺','😦','😧','😨','😰','😥','😢','😭','😱','😖','😣','😞','😓','😩','😫','🥱','😤','😡','🤬','👍','👎','👏','🙏','🤝','❤️','🔥','⭐','✅','❌','⚡','💡','🎉','🚀']

const STATUS_LABELS: Record<string, string> = {
  new: 'Новый',
  in_progress: 'В работе',
  waiting: 'Ожидает ответа',
  resolved: 'Решён',
  closed: 'Закрыт',
  reopened: 'Переоткрыт',
}

const STATUS_COLORS: Record<string, string> = {
  new: '#3b82f6',
  in_progress: '#f59e0b',
  waiting: '#8b5cf6',
  resolved: '#22c55e',
  closed: '#6b7280',
  reopened: '#ef4444',
}

type View = 'menu' | 'history' | 'detail' | 'create' | 'form'

interface FileItem {
  file: File
  id: string
  preview?: string
}

export default function SupportWidget() {
  const { user } = useAuth()
  const [open, setOpen] = useState(false)
  // Открыть чат поддержки из любого места интерфейса: window.dispatchEvent(new Event('support:open'))
  // (например, «Нет доступа к старой почте?» в смене почты).
  useEffect(() => {
    const onOpen = () => setOpen(true)
    window.addEventListener('support:open', onOpen)
    return () => window.removeEventListener('support:open', onOpen)
  }, [])
  // Закрыли чат поддержки — сигнал тем, кто ждал (окна «Отказов нейросетей» не всплывают поверх переписки).
  useEffect(() => {
    if (!open) window.dispatchEvent(new Event('support:closed'))
  }, [open])
  const [view, setView] = useState<View>('menu')
  const [tickets, setTickets] = useState<Ticket[]>([])
  const [selectedTicket, setSelectedTicket] = useState<TicketDetail | null>(null)
  const [loading, setLoading] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')

  const [category, setCategory] = useState('')
  const [service, setService] = useState('')
  const [messageText, setMessageText] = useState('')
  const [files, setFiles] = useState<FileItem[]>([])
  const [showEmoji, setShowEmoji] = useState(false)
  const [showServicePicker, setShowServicePicker] = useState(false)
  const [sending, setSending] = useState(false)
  const [replyText, setReplyText] = useState('')

  const fileInputRef = useRef<HTMLInputElement>(null)
  const emojiRef = useRef<HTMLDivElement>(null)
  const dropRef = useRef<HTMLDivElement>(null)
  const messagesEndRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) {
      setView('menu')
      setCategory('')
      setService('')
      setMessageText('')
      setFiles([])
      setShowEmoji(false)
      setSelectedTicket(null)
    }
  }, [open])

  useEffect(() => {
    if (selectedTicket && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' })
    }
  }, [selectedTicket])

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (emojiRef.current && !emojiRef.current.contains(e.target as Node)) {
        setShowEmoji(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const loadTickets = useCallback(async () => {
    if (!user) return
    setLoading(true)
    try {
      const data = await listTickets()
      setTickets(data.tickets)
    } catch (e) {
      console.error(e)
    } finally {
      setLoading(false)
    }
  }, [user])

  const openHistory = async () => {
    setView('history')
    await loadTickets()
  }

  const openTicketDetail = async (id: string) => {
    setLoading(true)
    try {
      const data = await getTicket(id)
      setSelectedTicket(data)
      setView('detail')
    } catch (e) {
      console.error(e)
    } finally {
      setLoading(false)
    }
  }

  const handleCreate = () => {
    setCategory('')
    setService('')
    setMessageText('')
    setFiles([])
    setView('create')
  }

  const selectCategory = (catId: string) => {
    setCategory(catId)
    setView('form')
  }

  const handleSendTicket = async () => {
    if (!messageText.trim()) return
    setSending(true)
    try {
      const ticket = await createTicket({
        category,
        service: service || undefined,
        subject: CATEGORIES.find(c => c.id === category)?.title,
        message: messageText.trim(),
      })

      if (files.length > 0 && ticket.messages.length > 0) {
        const msgId = ticket.messages[0].id
        for (const f of files) {
          await uploadAttachment(ticket.id, f.file, msgId)
        }
      }

      setSelectedTicket(ticket)
      setView('detail')
      setMessageText('')
      setFiles([])
    } catch (e) {
      console.error(e)
    } finally {
      setSending(false)
    }
  }

  const handleSendReply = async () => {
    if (!replyText.trim() || !selectedTicket) return
    setSending(true)
    try {
      await sendMessage(selectedTicket.id, replyText.trim())
      const updated = await getTicket(selectedTicket.id)
      setSelectedTicket(updated)
      setReplyText('')
    } catch (e) {
      console.error(e)
    } finally {
      setSending(false)
    }
  }

  const handleReopen = async () => {
    if (!selectedTicket) return
    try {
      await reopenTicket(selectedTicket.id)
      const updated = await getTicket(selectedTicket.id)
      setSelectedTicket(updated)
    } catch (e) {
      console.error(e)
    }
  }

  const addFiles = (newFiles: FileList | File[]) => {
    const items: FileItem[] = Array.from(newFiles).map(f => ({
      file: f,
      id: Math.random().toString(36).slice(2),
      preview: f.type.startsWith('image/') ? URL.createObjectURL(f) : undefined,
    }))
    setFiles(prev => [...prev, ...items])
  }

  const removeFile = (id: string) => {
    setFiles(prev => {
      const item = prev.find(f => f.id === id)
      if (item?.preview) URL.revokeObjectURL(item.preview)
      return prev.filter(f => f.id !== id)
    })
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (dropRef.current) dropRef.current.classList.add('support-drop-active')
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    if (dropRef.current) dropRef.current.classList.remove('support-drop-active')
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (dropRef.current) dropRef.current.classList.remove('support-drop-active')
    if (e.dataTransfer.files.length) addFiles(e.dataTransfer.files)
  }

  const insertEmoji = (emoji: string) => {
    setMessageText(prev => prev + emoji)
    setShowEmoji(false)
  }

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr)
    return d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' }) +
      ', ' + d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })
  }

  const formatSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' Б'
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(0) + ' КБ'
    return (bytes / (1024 * 1024)).toFixed(1) + ' МБ'
  }

  const filteredTickets = tickets.filter(t =>
    !searchQuery || t.subject?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    t.last_message?.toLowerCase().includes(searchQuery.toLowerCase())
  )

  const openTickets = filteredTickets.filter(t => !['resolved', 'closed'].includes(t.status))
  const closedTickets = filteredTickets.filter(t => ['resolved', 'closed'].includes(t.status))

  if (!user) return null

  return (
    <>
      <button className="support-trigger" onClick={() => setOpen(true)} aria-label="Техподдержка" />

      {open && (
        <div className="support-overlay" onClick={() => setOpen(false)}>
          <div className="support-modal" onClick={e => e.stopPropagation()}>
            <button className="support-close" onClick={() => setOpen(false)}><X size={20} /></button>

            {view === 'menu' && (
              <div className="support-menu">
                <h2>Техподдержка</h2>
                <p>Чем можем помочь?</p>
                <div className="support-menu-buttons">
                  <button onClick={openHistory}>
                    <MessageSquare size={20} />
                    <span>Центр поддержки</span>
                    <span className="support-menu-hint">История обращений</span>
                  </button>
                  <button onClick={handleCreate}>
                    <Plus size={20} />
                    <span>Написать в поддержку</span>
                    <span className="support-menu-hint">Создать новый тикет</span>
                  </button>
                </div>
              </div>
            )}

            {view === 'history' && (
              <div className="support-history">
                <div className="support-history-header">
                  <button className="support-back" onClick={() => setView('menu')}><ChevronLeft size={20} /></button>
                  <h2>Центр поддержки</h2>
                  <button className="support-create-btn" onClick={handleCreate}>Создать тикет</button>
                </div>

                <div className="support-search">
                  <Search size={16} />
                  <input placeholder="Поиск" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} />
                </div>

                {loading ? (
                  <div className="support-loading">Загрузка...</div>
                ) : tickets.length === 0 ? (
                  <div className="support-empty">
                    <MessageSquare size={40} />
                    <p>Обращений пока нет</p>
                    <button onClick={handleCreate}>Создать тикет</button>
                  </div>
                ) : (
                  <div className="support-ticket-list">
                    {openTickets.length > 0 && (
                      <div className="support-ticket-group">
                        <div className="support-ticket-group-title">Открытые тикеты <span>{openTickets.length}</span></div>
                        {openTickets.map(t => (
                          <div key={t.id} className="support-ticket-item" onClick={() => openTicketDetail(t.id)}>
                            <div className="support-ticket-item-status" style={{ background: STATUS_COLORS[t.status] || '#6b7280' }} />
                            <div className="support-ticket-item-body">
                              <div className="support-ticket-item-title">{t.subject || 'Без темы'}</div>
                              <div className="support-ticket-item-preview">{t.last_message || '—'}</div>
                              <div className="support-ticket-item-meta">
                                <span>{STATUS_LABELS[t.status] || t.status}</span>
                                <span>{formatDate(t.updated_at)}</span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    {closedTickets.length > 0 && (
                      <div className="support-ticket-group">
                        <div className="support-ticket-group-title">Закрытые тикеты <span>{closedTickets.length}</span></div>
                        {closedTickets.map(t => (
                          <div key={t.id} className="support-ticket-item" onClick={() => openTicketDetail(t.id)}>
                            <div className="support-ticket-item-status" style={{ background: STATUS_COLORS[t.status] || '#6b7280' }} />
                            <div className="support-ticket-item-body">
                              <div className="support-ticket-item-title">{t.subject || 'Без темы'}</div>
                              <div className="support-ticket-item-preview">{t.last_message || '—'}</div>
                              <div className="support-ticket-item-meta">
                                <span>{STATUS_LABELS[t.status] || t.status}</span>
                                <span>{formatDate(t.updated_at)}</span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {view === 'detail' && selectedTicket && (
              <div className="support-detail">
                <div className="support-detail-header">
                  <button className="support-back" onClick={() => { setView('history'); loadTickets() }}><ChevronLeft size={20} /></button>
                  <div>
                    <h3>{selectedTicket.subject || 'Без темы'}</h3>
                    <div className="support-detail-meta">
                      №{selectedTicket.ticket_number} ·{' '}
                      <span style={{ color: STATUS_COLORS[selectedTicket.status] }}>{STATUS_LABELS[selectedTicket.status] || selectedTicket.status}</span>
                    </div>
                  </div>
                </div>

                <div className="support-messages">
                  {selectedTicket.messages.map((m: Message) => (
                    <div key={m.id} className={`support-message ${m.sender_type === 'admin' ? 'support-message-admin' : 'support-message-user'}`}>
                      <div className="support-message-bubble">
                        <div className="support-message-text">{m.message}</div>
                        {m.attachments.length > 0 && (
                          <div className="support-message-attachments">
                            {m.attachments.map(a => (
                              <a key={a.id} href={a.url} target="_blank" rel="noopener noreferrer" className="support-attachment-link">
                                <Paperclip size={14} />
                                <span>{a.filename}</span>
                                <span className="support-attachment-size">{formatSize(a.size_bytes)}</span>
                              </a>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="support-message-time">
                        {m.sender_type === 'admin' ? 'Поддержка' : 'Вы'} · {formatDate(m.created_at)}
                      </div>
                    </div>
                  ))}
                  <div ref={messagesEndRef} />
                </div>

                {['closed', 'resolved'].includes(selectedTicket.status) ? (
                  <div className="support-detail-closed">
                    <span>Тикет закрыт</span>
                    <button onClick={handleReopen}>Мой вопрос не решён</button>
                  </div>
                ) : (
                  <div className="support-reply">
                    <input
                      placeholder="Написать сообщение..."
                      value={replyText}
                      onChange={e => setReplyText(e.target.value)}
                      onKeyDown={e => { if (shouldSendOnEnter(e)) { e.preventDefault(); handleSendReply() } }}
                    />
                    <button onClick={handleSendReply} disabled={!replyText.trim() || sending}>
                      <Send size={18} />
                    </button>
                  </div>
                )}
              </div>
            )}

            {view === 'create' && (
              <div className="support-create">
                <div className="support-create-header">
                  <button className="support-back" onClick={() => setView('menu')}><ChevronLeft size={20} /></button>
                  <h2>Создать тикет</h2>
                </div>

                <div className="support-categories">
                  {CATEGORIES.map(cat => (
                    <button
                      key={cat.id}
                      className={`support-category ${category === cat.id ? 'active' : ''}`}
                      onClick={() => selectCategory(cat.id)}
                    >
                      <span className="support-category-title">{cat.title}</span>
                    </button>
                  ))}
                </div>

                {category && (
                  <div className="support-category-hints">
                    <p>По возможности укажите:</p>
                    {CATEGORIES.find(c => c.id === category)?.hints.map((h, i) => (
                      <span key={i}>{h}</span>
                    ))}
                  </div>
                )}
              </div>
            )}

            {view === 'form' && (
              <div className="support-form">
                <div className="support-form-header">
                  <button className="support-back" onClick={() => setView('create')}><ChevronLeft size={20} /></button>
                  <h2>{CATEGORIES.find(c => c.id === category)?.title}</h2>
                </div>

                <div className="support-form-body">
                  <label>Выберите сервис</label>
                  <div className="support-service-select" onClick={() => setShowServicePicker(!showServicePicker)}>
                    <span>{service || 'Не выбрано'}</span>
                    <ChevronLeft size={16} style={{ transform: 'rotate(-90deg)' }} />
                  </div>

                  {showServicePicker && (
                    <div className="support-service-picker">
                      {Object.entries(SERVICES).map(([group, items]) => (
                        <div key={group} className="support-service-group">
                          <div className="support-service-group-title">{group}</div>
                          {items.map(item => (
                            <div
                              key={item}
                              className={`support-service-item ${service === item ? 'active' : ''}`}
                              onClick={() => { setService(item); setShowServicePicker(false) }}
                            >
                              {item}
                            </div>
                          ))}
                        </div>
                      ))}
                    </div>
                  )}

                  <label>Сообщение</label>
                  <div className="support-textarea-wrap">
                    <textarea
                      placeholder="Напишите ваш вопрос. Скриншоты, выводы из консоли и другие подробности ускорят наш ответ"
                      value={messageText}
                      onChange={e => setMessageText(e.target.value)}
                      rows={5}
                    />
                    <div className="support-textarea-actions">
                      <div className="support-emoji-wrap" ref={emojiRef}>
                        <button className="support-emoji-btn" onClick={() => setShowEmoji(!showEmoji)}><Smile size={18} /></button>
                        {showEmoji && (
                          <div className="support-emoji-picker">
                            <div className="support-emoji-title">Смайлы</div>
                            <div className="support-emoji-grid">
                              {EMOJIS.map((e, i) => (
                                <button key={i} onClick={() => insertEmoji(e)}>{e}</button>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                      <button className="support-attach-btn" onClick={() => fileInputRef.current?.click()}>
                        <Paperclip size={18} />
                      </button>
                    </div>
                  </div>

                  <div
                    ref={dropRef}
                    className="support-dropzone"
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <span className="support-dropzone-link">Выберите файл</span>{' '}или перетащите его сюда
                    <div className="support-dropzone-hint">Не более 50 МБ</div>
                  </div>

                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    hidden
                    onChange={e => { if (e.target.files) addFiles(e.target.files); e.target.value = '' }}
                  />

                  {files.length > 0 && (
                    <div className="support-files">
                      {files.map(f => (
                        <div key={f.id} className="support-file-item">
                          {f.preview && <img src={f.preview} alt="" />}
                          <span>{f.file.name}</span>
                          <span className="support-file-size">{formatSize(f.file.size)}</span>
                          <button onClick={() => removeFile(f.id)}><X size={14} /></button>
                        </div>
                      ))}
                    </div>
                  )}

                  <button
                    className="support-submit"
                    disabled={!messageText.trim() || sending}
                    onClick={handleSendTicket}
                  >
                    {sending ? 'Отправка...' : 'Отправить'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  )
}